// Ship's Papers — shared bits (styles, formatters, small controls).
import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import { isAxiosError } from 'axios';
import type { Section, ReadinessItem } from '../../types/papers';

export const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-40';
export const btnPrimary = `${btn} bg-amber-400 text-slate-900 hover:brightness-105`;
export const btnGhost = `${btn} border border-slate-600 text-white hover:border-amber-400`;
export const btnDanger = `${btn} border border-red-400/60 text-red-300 hover:bg-red-900/30`;
export const input = 'w-full rounded-lg bg-slate-900 border border-slate-600 text-white text-base px-3 py-2 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400/70 disabled:opacity-60';
export const label = 'block text-sm font-semibold text-slate-200';
export const modalWrap = 'fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 p-4';
export const modalBox = 'bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full space-y-4 max-h-[92vh] overflow-y-auto';

export const errMsg = (e: unknown, fallback: string) => (isAxiosError(e) && (e.response?.data as { error?: string } | undefined)?.error) || fallback;

export const fmtBytes = (b: number | null | undefined) => {
  if (b == null) return '—';
  const u = ['B', 'KB', 'MB', 'GB']; let v = b, i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i >= 2 ? 1 : 0)} ${u[i]}`;
};
export const fmtDate = (iso: string | null | undefined) => (iso ? new Date(iso.length === 10 ? iso + 'T00:00:00' : iso).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
export const fmtAgo = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = (Date.now() - new Date(iso).getTime()) / 86_400_000;
  if (d < 0) return fmtDate(iso);   // a future date is a date, not "today"
  if (d < 1) return 'today'; if (d < 2) return 'yesterday'; if (d < 30) return `${Math.floor(d)} d ago`;
  return fmtDate(iso);
};
/**
 * Whole calendar days from today (on this browser's clock, or `timeZone`) to a
 * YYYY-MM-DD date: 0 all through that date, −1 from the next day. A document is
 * valid through its expiry date — the same rule as the API (lib/expiryDays.ts).
 */
export const daysUntil = (iso: string, nowMs: number = Date.now(), timeZone?: string) => {
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(new Date(nowMs)).map((x) => [x.type, x.value]));
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day))) / 86_400_000);
};

export const SECTION_ICON: Record<Section, string> = { vessel: '⚓', certificates: '🛡️', ports: '🛃', equipment: '🔧', crew: '👥', other: '📁' };

/** Expiry pill: red past, amber inside 90 days, quiet otherwise. */
export function ExpiryPill({ expiresOn }: { expiresOn: string | null | undefined }) {
  if (!expiresOn) return null;
  const d = daysUntil(expiresOn);
  const cls = d < 0 ? 'border-red-400/60 text-red-300' : d <= 90 ? 'border-amber-400/60 text-amber-300' : 'border-slate-600 text-slate-400';
  const text = d < 0 ? `expired ${Math.abs(d)} d ago` : d === 0 ? 'expires today' : d <= 90 ? `${d} d left` : `until ${fmtDate(expiresOn)}`;
  return <span className={`inline-flex text-[13px] font-semibold border rounded-full px-2 py-0.5 whitespace-nowrap ${cls}`}>{text}</span>;
}

/** Said on a copy whose shared library Vortex has taken down (Tony, 2026-09-25: flag, don't delete). */
export const LIBRARY_TAKEN_DOWN = 'The library this came from was taken down by Vortex';

/** List badge for such a copy. */
/** A copy taken from a shared library: it can't be published again, only its original can. */
export function FromLibraryBadge() {
  return <span title="Copied from a shared library — only its original can be published" className="inline-flex text-[13px] font-semibold border border-sky-400/60 text-sky-300 rounded-full px-2 py-0.5 whitespace-nowrap">From library</span>;
}

export function TakenDownBadge() {
  return <span title={LIBRARY_TAKEN_DOWN} className="inline-flex text-[13px] font-semibold border border-red-400/60 text-red-300 rounded-full px-2 py-0.5 whitespace-nowrap">Library taken down</span>;
}

/** Document-panel notice for such a copy: what happened, and that the copy is still theirs. */
export function TakenDownNotice() {
  return (
    <p role="status" className="text-sm text-red-200 bg-red-900/30 border border-red-800/60 rounded-lg px-3 py-2">
      {LIBRARY_TAKEN_DOWN}. This copy is still yours to keep or delete; it will not be updated from the library.
    </p>
  );
}

export function StatusPill({ status }: { status: ReadinessItem['status'] }) {
  const map: Record<ReadinessItem['status'], [string, string]> = {
    current: ['Current', 'border-emerald-400/60 text-emerald-300'], expiring: ['Expiring', 'border-amber-400/60 text-amber-300'],
    expired: ['Expired', 'border-red-400/60 text-red-300'], missing: ['Missing', 'border-red-400/60 text-red-300'], na: ['Not applicable', 'border-slate-600 text-slate-400'],
  };
  const [text, cls] = map[status];
  return <span className={`inline-flex text-[13px] font-semibold border rounded-full px-2 py-0.5 ${cls}`}>{text}</span>;
}

/**
 * Inline document preview with an Expand button — certificates photographed
 * on a phone are unreadable in a side-panel box. Expanded = near-fullscreen
 * overlay; Esc or backdrop click closes just the overlay, not the preview.
 */
export function PreviewPane({ url, mime, name, onClose }: { url: string; mime: string; name: string; onClose: () => void }) {
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);
  const body = (big: boolean) =>
    mime.startsWith('image/')
      ? <img src={url} alt={name} className={big ? 'max-h-full max-w-full object-contain' : 'max-h-[60vh] w-full object-contain'} />
      : <iframe title={name} src={url} className={big ? 'w-full h-full bg-white' : 'w-full h-[60vh]'} />;
  return (
    <>
      <div className="rounded-xl overflow-hidden border border-slate-700 bg-black/40">
        <div className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm text-slate-300">
          <span className="truncate">{name}</span>
          <span className="flex items-center gap-3 shrink-0">
            <button type="button" onClick={() => setExpanded(true)} className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200 font-semibold" aria-label={`Expand ${name}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M20.25 3.75v4.5m0-4.5h-4.5m4.5 0L15 9m5.25 11.25v-4.5m0 4.5h-4.5m4.5 0L15 15m-11.25 5.25v-4.5m0 4.5h4.5m-4.5 0L9 15" /></svg>
              Expand
            </button>
            <button type="button" onClick={onClose} className="text-slate-400 hover:text-white">Close</button>
          </span>
        </div>
        {body(false)}
      </div>
      {expanded && (
        <div className="fixed inset-0 z-[3000] bg-black/85 flex flex-col" role="dialog" aria-modal="true" aria-label={name} onClick={(e) => { if (e.target === e.currentTarget) setExpanded(false); }}>
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 text-slate-200">
            <span className="truncate text-base font-semibold">{name}</span>
            <button type="button" onClick={() => setExpanded(false)} className="shrink-0 rounded-lg border border-slate-600 px-3 py-1.5 text-sm font-semibold hover:bg-slate-800">Close (Esc)</button>
          </div>
          <div className="flex-1 min-h-0 flex items-center justify-center p-3" onClick={(e) => { if (e.target === e.currentTarget) setExpanded(false); }}>
            {body(true)}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Centred document viewer. A library can hold hundreds of documents, so an
 * inline pane below the list is unusable — you scroll past everything to reach
 * it and back again to pick the next one. This opens over the list and leaves
 * your scroll position alone.
 *
 * PDFs get the browser's own viewer (its scroll/zoom/print are better than
 * anything we would reimplement); images get explicit zoom because a
 * photographed certificate is unreadable fitted to the box.
 */
export function PreviewModal({ url, mime, name, onClose }: { url: string; mime: string; name: string; onClose: () => void }) {
  const isImage = mime.startsWith('image/');
  const [zoom, setZoom] = useState(1);
  const rootRef = useRef<HTMLDivElement>(null);
  useDialog(rootRef, onClose);
  useEffect(() => {
    const prev = document.body.style.overflow;      // don't scroll the list behind the modal
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
  const zoomBtn = 'px-2 py-1 rounded-md border border-slate-600 text-white hover:border-amber-400 disabled:opacity-40';
  return (
    <div ref={rootRef} tabIndex={-1} className="fixed inset-0 z-[3000] bg-black/80 flex items-center justify-center p-3 sm:p-6 focus:outline-none"
         role="dialog" aria-modal="true" aria-label={name}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-6xl h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-700">
          <span className="flex-1 min-w-0 truncate text-base font-semibold text-white" title={name}>{name}</span>
          {isImage && (
            <span className="flex items-center gap-1 shrink-0">
              <button type="button" onClick={() => setZoom((z) => Math.max(0.25, +(z - 0.25).toFixed(2)))} disabled={zoom <= 0.25} className={zoomBtn} aria-label="Zoom out">−</button>
              <span className="text-sm text-slate-300 w-14 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
              <button type="button" onClick={() => setZoom((z) => Math.min(5, +(z + 0.25).toFixed(2)))} disabled={zoom >= 5} className={zoomBtn} aria-label="Zoom in">+</button>
              <button type="button" onClick={() => setZoom(1)} className={`${zoomBtn} text-sm`}>Fit</button>
            </span>
          )}
          <a href={url} download={name} className={`${btnGhost} shrink-0`}>Download</a>
          <button type="button" onClick={onClose} className={`${btnPrimary} shrink-0`}>Close (Esc)</button>
        </div>
        <div className={`flex-1 min-h-0 bg-black/40 ${isImage ? 'overflow-auto p-3' : ''}`}>
          {isImage
            ? <img src={url} alt={name} style={{ width: `${zoom * 100}%` }} className="mx-auto max-w-none object-contain" />
            : <iframe title={name} src={url} className="w-full h-full bg-white border-0" />}
        </div>
      </div>
    </div>
  );
}

// ---------------- dialogs: Escape, focus trap, focus restore ----------------

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';
/** jsdom lays nothing out, so every element there has no client rects. */
const NO_LAYOUT = typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent);
const focusables = (root: HTMLElement) =>
  Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => NO_LAYOUT || el.getClientRects().length > 0);

/** Open dialogs, oldest first. Only the top one answers Escape and Tab. */
const openDialogs: HTMLElement[] = [];
const topDialog = () => {
  let top = openDialogs[openDialogs.length - 1];
  // A dialog opened inside another (same commit, effects run child first) is on top of it.
  for (let moved = true; moved && top;) {
    moved = false;
    for (const d of openDialogs) if (d !== top && top.contains(d)) { top = d; moved = true; }
  }
  return top;
};

/**
 * Keyboard behaviour every Ship's Papers dialog shares: focus moves in when it
 * opens (an autoFocus field keeps it), Tab and Shift+Tab stay inside, Escape
 * calls `onEscape` (top dialog only), and focus returns to whatever opened it.
 */
export function useDialog(ref: RefObject<HTMLElement>, onEscape: () => void) {
  const escape = useRef(onEscape);
  escape.current = onEscape;
  // Captured during the first render — before any autoFocus child takes focus.
  const [opener] = useState(() => (typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null));
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    openDialogs.push(root);
    if (!root.contains(document.activeElement)) (focusables(root)[0] ?? root).focus();
    const onKey = (e: KeyboardEvent) => {
      if (topDialog() !== root) return;
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); escape.current(); return; }
      if (e.key !== 'Tab') return;
      const els = focusables(root);
      if (!els.length) { e.preventDefault(); root.focus(); return; }
      const first = els[0];
      const last = els[els.length - 1];
      const active = document.activeElement;
      const outside = !root.contains(active);
      if (e.shiftKey && (active === first || active === root || outside)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (active === last || outside)) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      const i = openDialogs.indexOf(root);
      if (i >= 0) openDialogs.splice(i, 1);
      if (opener?.isConnected) opener.focus();
    };
  }, [ref, opener]);
}

/**
 * A centred dialog. `dirty`: the form inside holds typing the user would lose,
 * so a backdrop click or Escape asks first instead of closing (the dialog's own
 * Cancel button still closes straight away — that is a deliberate choice).
 */
export function Modal({ title, children, onClose, wide, dirty }: { title: string; children: React.ReactNode; onClose: () => void; wide?: boolean; dirty?: boolean }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const keepRef = useRef<HTMLButtonElement>(null);
  const tryClose = () => { if (dirty) setConfirmDiscard(true); else onClose(); };
  useDialog(rootRef, () => { if (confirmDiscard) setConfirmDiscard(false); else tryClose(); });
  useEffect(() => { if (confirmDiscard) keepRef.current?.focus(); }, [confirmDiscard]);
  return (
    <div ref={rootRef} tabIndex={-1} className={`${modalWrap} focus:outline-none`} role="dialog" aria-modal="true" aria-label={title} aria-labelledby={titleId}
      onClick={(e) => { if (e.target === e.currentTarget) tryClose(); }}>
      <div className={`${modalBox} ${wide ? 'max-w-3xl' : 'max-w-lg'}`}>
        <h3 id={titleId} className="text-lg font-bold text-white">{title}</h3>
        {confirmDiscard && (
          <div role="alert" className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-400/60 bg-amber-400/10 px-3 py-2">
            <span className="flex-1 min-w-[180px] text-sm text-amber-100">Discard what you have entered?</span>
            <button ref={keepRef} type="button" onClick={() => setConfirmDiscard(false)} className={btnGhost}>Keep editing</button>
            <button type="button" onClick={() => { setConfirmDiscard(false); onClose(); }} className={btnDanger}>Discard</button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
